import { useCallback, useEffect, useSyncExternalStore } from 'react';

import en from '../locales/en.json';
import ar from '../locales/ar.json';

const translations = { en, ar };
const LANG_KEY = 'palc_lang';
const SUPPORTED = ['en', 'ar'];
const DEFAULT_LANG = 'ar'; // Arabic-first institutional site

function readStoredLang() {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch (_) {
    // ignore storage access errors (private mode, etc.)
  }
  return DEFAULT_LANG;
}

let currentLang = readStoredLang();
const listeners = new Set();

function emit() {
  listeners.forEach((cb) => cb());
}

export function getDirection(lang) {
  return lang === 'ar' ? 'rtl' : 'ltr';
}

export function setLang(lang) {
  if (!SUPPORTED.includes(lang) || lang === currentLang) return;
  currentLang = lang;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch (_) {
    // ignore
  }
  emit();
}

function subscribe(cb) {
  listeners.add(cb);
  const onStorage = (e) => {
    if (e.key === LANG_KEY) {
      currentLang = readStoredLang();
      cb();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot() {
  return currentLang;
}

export default function useTranslation() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const t = useCallback(
    (key) => {
      const dict = translations[lang] || translations.en;
      return dict[key] ?? translations.en[key] ?? key;
    },
    [lang],
  );

  useEffect(() => {
    document.documentElement.dir = getDirection(lang);
    document.documentElement.lang = lang;
  }, [lang]);

  return { t, lang, setLang, dir: getDirection(lang) };
}
